import { useRouteMatch } from 'react-router-dom';

import PropTypes from 'prop-types';

import { Box, Paper, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import BanMemberContainer from '../BanMember/BanMemberContainer';

import SearchComponent from '../../Table/SearchComponent';
import CommunityMemberComponent from './CommunityMemberComponent';

import { isAdmin, isPM } from '../../../utils';

import { MY_COMMUNITY_MEMBERS } from '../constants';

import { ButtonLink } from '../../Button';
import BanUserButton from '../components/BanUserButton';
import CommunityMembersButton from '../components/CommunityMembersButton';
import MessagesButton from '../components/MessagesButton';
import CommunityMembersSkeleton from './CommunityMembersSkeleton';

const StyledContainer = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: '1rem',
  padding: '1.5rem 2rem',
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
  },
}));

const StyledButton = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    marginBottom: 0,
  },
}));

const CommunityMembersComponent = (props) => {
  const theme = useTheme();
  const match = useRouteMatch();

  const {
    search,
    setSearch,
    loadMore,
    isLoading,
    isLoadingMore,
    isMobileScreen,
    communityMembersList,
    nextCommunityMembers,
    setShowCommunityMembers,
    showCommunityMembers,
    onMessagesClick,
    hasNewNotifications,
    isModalOpen,
    setIsModalOpen,
    communityId,
  } = props;

  return (
    <StyledContainer elevation={0}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        {!isAdmin() && (
          <StyledButton>
            <MessagesButton
              onClick={onMessagesClick}
              hasNewNotifications={hasNewNotifications}
            />
          </StyledButton>
        )}
        {(isAdmin() || isPM(match?.params)) && (
          <StyledButton>
            <BanUserButton onClick={() => setIsModalOpen(true)} />
          </StyledButton>
        )}
      </Box>

      {isModalOpen && (
        <StyledButton>
          <BanMemberContainer
            communityId={communityId}
            onModalClose={() => setIsModalOpen(false)}
          />
        </StyledButton>
      )}
      {isMobileScreen && (
        <CommunityMembersButton
          onClick={() => setShowCommunityMembers(!showCommunityMembers)}
        />
      )}
      {showCommunityMembers && (
        <Box width="100%">
          <Typography
            variant="h6"
            textTransform="uppercase"
            color={theme.palette.primary.main}
          >
            {MY_COMMUNITY_MEMBERS}
          </Typography>
          <SearchComponent
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Box mt="1rem" />
          {!communityMembersList?.length && isLoading && (
            <Box mt="3rem">
              {Array.from({ length: 10 }, (v, i) => i).map((i) => (
                <CommunityMembersSkeleton key={i} />
              ))}
            </Box>
          )}
          <Box display="flex" flexDirection="column">
            {communityMembersList?.map((person) => (
              <CommunityMemberComponent key={person?.id} person={person} />
            ))}
          </Box>
          {isLoadingMore && (
            <Box mt="2rem">
              <CommunityMembersSkeleton />
            </Box>
          )}
          {!communityMembersList?.length && !isLoading && (
            <Typography textAlign="center">
              No community members to display
            </Typography>
          )}
          {!!nextCommunityMembers && (
            <Box display="flex" justifyContent="center" mt="2rem">
              <ButtonLink onClick={loadMore}>Load more</ButtonLink>
            </Box>
          )}
        </Box>
      )}
    </StyledContainer>
  );
};

CommunityMembersComponent.defaultProps = {
  nextCommunityMembers: null,
};

CommunityMembersComponent.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func.isRequired,
  loadMore: PropTypes.func.isRequired,
  communityMembersList: PropTypes.instanceOf(Array).isRequired,
  nextCommunityMembers: PropTypes.instanceOf(Object),
  setShowCommunityMembers: PropTypes.func.isRequired,
  showCommunityMembers: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
  onMessagesClick: PropTypes.func.isRequired,
  hasNewNotifications: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  setIsModalOpen: PropTypes.func.isRequired,
  communityId: PropTypes.number.isRequired,
};
export default CommunityMembersComponent;
